import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { add, compareAsc, format, isAfter, isBefore } from 'date-fns';
import { PointOptionsObject } from 'highcharts';
import { LoHiDriverShiftOverview } from '../../types';

@Component({
  selector: 'lohi-shift-chart',
  templateUrl: './shift-chart.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftChartComponent implements OnInit {
  public clonedEarnings: LoHiDriverShiftOverview[];
  @Input() public earningsPerDay: LoHiDriverShiftOverview[];

  public get format(): string {
    return DATETIME_FORMAT;
  }

  public get timeFormat(): string {
    return TIME_FORMAT;
  }

  public get labels(): Array<string> {
    if (!this.earningsPerDay) {
      return [];
    }
    return this.clonedEarnings?.slice().map((value) => {
      const start = new Date(value.timestamp);
      const end = add(new Date(value.timestamp), { hours: 1 });
      if (isAfter(new Date(), start) && isBefore(new Date(), end)) {
        return 'Now';
      }
      return (
        format(start, this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }) +
        ' - ' +
        format(end, this.timeFormat, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        })
      );
    });
  }

  public chart: Chart;

  constructor() {}

  public ngOnInit() {
    this.clonedEarnings = this.earningsPerDay
      .slice()
      .sort((a, b) => compareAsc(new Date(a.timestamp), new Date(b.timestamp)));
    const labels = this.labels;
    const title = `<div class="text-lg font-bold">Commitment Tracker: Drivers on Shift</div>`;
    this.chart = new Chart({
      chart: {
        type: 'column',
      },
      title: {
        align: 'left',
        useHTML: true,
        text: title,
      },
      credits: {
        enabled: false,
      },
      yAxis: [
        {
          labels: {
            enabled: false,
          },
          plotLines: !!this.clonedEarnings[0].commitment
            ? [
                {
                  color: '#EF6048',
                  label: {
                    text: 'Commitment: ' + this.clonedEarnings[0]?.commitment + ' drivers',
                    style: {
                      color: '#EF6048',
                    },
                  },
                  dashStyle: 'Dash',
                  zIndex: 1000,
                  value: this.clonedEarnings[0]?.commitment,
                },
              ]
            : [],
          tickInterval: 0,
          title: { text: 'Drivers' },
        },
      ],
      plotOptions: {
        series: {
          stacking: 'normal',
        },
      },
      xAxis: {
        type: 'category',
        categories: labels,
        visible: true,
        labels: {
          autoRotation: [],
          rotation: 90,
          formatter: (that) => {
            if (that.value === 'Now') {
              return that.value;
            }
            return (that.value as string).split(' ').slice(1, 4).join(' ');
          },
        },
      },
      series: [],
    });

    const s: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const date of this.clonedEarnings.slice()) {
      let label =
        format(new Date(date.timestamp), this.format, {
          useAdditionalWeekYearTokens: true,
          useAdditionalDayOfYearTokens: true,
        }) +
        ' - ' +
        format(add(new Date(date.timestamp), { hours: 1 }), this.timeFormat, {
          useAdditionalWeekYearTokens: true,
          useAdditionalDayOfYearTokens: true,
        });
      const start = new Date(date.timestamp);
      const end = add(new Date(date.timestamp), { hours: 1 });
      if (isAfter(new Date(), start) && isBefore(new Date(), end)) {
        label = 'Now';
      }
      s.push({
        label,
        y: date.scheduledShiftCount,
      });
      /*s.push([
        format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        date.earnings as number,
      ]);*/
    }
    const s2: Array<[number | string, number | null] | PointOptionsObject> = [];
    for (const date of this.clonedEarnings.slice()) {
      let label =
        format(new Date(date.timestamp), this.format, {
          useAdditionalWeekYearTokens: true,
          useAdditionalDayOfYearTokens: true,
        }) +
        ' - ' +
        format(add(new Date(date.timestamp), { hours: 1 }), this.timeFormat, {
          useAdditionalWeekYearTokens: true,
          useAdditionalDayOfYearTokens: true,
        });
      const start = new Date(date.timestamp);
      const end = add(new Date(date.timestamp), { hours: 1 });
      if (isAfter(new Date(), start) && isBefore(new Date(), end)) {
        label = 'Now';
      }
      s2.push({
        label,
        y: date.actualShiftCount,
      });
      /*s2.push([
        format(new Date(date.date), this.format, {
          useAdditionalDayOfYearTokens: true,
          useAdditionalWeekYearTokens: true,
        }),
        date.lostEarnings as number,
      ]);*/
    }
    this.chart.addSeries(
      {
        name: 'Booked Shifts',
        type: 'column',
        color: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0, '#070707'],
            [1, '#3e3e52'],
          ],
        },
        data: s,
      },
      true,
      true,
    );
    this.chart.addSeries(
      {
        name: 'In Progress Shifts',
        type: 'column',
        color: '#448AFF',
        data: s2,
      },
      true,
      true,
    );
  }
}

const DATETIME_FORMAT = 'M/dd/yyyy ha';
const TIME_FORMAT = 'ha';
